<template>
  <ElMenu
    :default-active="defaultActive"
    :default-openeds="defaultOpeneds"
    :unique-opened="uniqueOpened"
    :menu-trigger="menuTrigger"
    :mode="mode"
    :collapse="collapse"
    :background-color="backgroundColor"
    :text-color="textColor"
    :active-text-color="activeTextColor"
    :collapse-transition="collapseTransition"
    :ellipsis="ellipsis"
    :popper-append-to-body="popperAppendToBody"
    :popper-offset="popperOffset"
    :router="router"
    @select="handleSelect"
    @open="handleOpen"
    @close="handleClose"
  >
    <template v-for="item in menuItems" :key="item.index">
      <!-- 子菜单 -->
      <ElSubMenu
        v-if="item.children && item.children.length > 0"
        :index="item.index"
        :disabled="item.disabled"
        :popper-class="item.popperClass"
        :show-timeout="item.showTimeout"
        :hide-timeout="item.hideTimeout"
      >
        <template #title>
          <ElIcon v-if="item.icon">
            <component :is="item.icon" />
          </ElIcon>
          <span>{{ item.title }}</span>
        </template>

        <!-- 递归渲染子菜单项 -->
        <MenuItem
          v-for="child in item.children"
          :key="child.index"
          :item="child"
        />
      </ElSubMenu>

      <!-- 菜单组 -->
      <ElMenuItemGroup
        v-else-if="item.type === 'group'"
        :title="item.title"
      >
        <MenuItem
          v-for="child in item.children"
          :key="child.index"
          :item="child"
        />
      </ElMenuItemGroup>

      <!-- 分割线 -->
      <li v-else-if="item.type === 'divider'" class="menu-divider"></li>

      <!-- 普通菜单项 -->
      <ElMenuItem
        v-else
        :index="item.index"
        :route="item.route"
        :disabled="item.disabled"
      >
        <ElIcon v-if="item.icon">
          <component :is="item.icon" />
        </ElIcon>
        <template #title>
          <span>{{ item.title }}</span>
          <ElBadge
            v-if="item.badge"
            :value="item.badge.value"
            :type="item.badge.type"
            :is-dot="item.badge.isDot"
            class="menu-badge"
          />
        </template>
      </ElMenuItem>
    </template>
  </ElMenu>
</template>

<script setup lang="ts">
/**
 * Menu 导航菜单组件
 * 基于 Element Plus Menu 组件封装
 * @author AI Assistant
 * @date 2024-01-01
 */

import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

// 徽章配置接口
interface MenuBadge {
  /** 徽章值 */
  value?: string | number
  /** 徽章类型 */
  type?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  /** 是否显示小圆点 */
  isDot?: boolean
}

// 菜单项接口
interface MenuItem {
  /** 唯一标识 */
  index: string
  /** 菜单标题 */
  title: string
  /** 菜单图标 */
  icon?: any
  /** 路由信息 */
  route?: RouteLocationRaw
  /** 是否禁用 */
  disabled?: boolean
  /** 菜单类型 */
  type?: 'item' | 'group' | 'divider'
  /** 子菜单 */
  children?: MenuItem[]
  /** 弹出菜单的自定义类名 */
  popperClass?: string
  /** 展开子菜单的延时 */
  showTimeout?: number
  /** 收起子菜单的延时 */
  hideTimeout?: number
  /** 徽章配置 */
  badge?: MenuBadge
}

// 接口定义
interface Props {
  /** 当前激活菜单的 index */
  defaultActive?: string
  /** 当前打开的 sub-menu 的 index 的数组 */
  defaultOpeneds?: string[]
  /** 是否只保持一个子菜单的展开 */
  uniqueOpened?: boolean
  /** 子菜单打开的触发方式 */
  menuTrigger?: 'hover' | 'click'
  /** 模式 */
  mode?: 'horizontal' | 'vertical'
  /** 是否水平折叠收起菜单 */
  collapse?: boolean
  /** 菜单的背景色 */
  backgroundColor?: string
  /** 菜单的文字颜色 */
  textColor?: string
  /** 当前激活菜单的文字颜色 */
  activeTextColor?: string
  /** 是否开启折叠动画 */
  collapseTransition?: boolean
  /** 是否省略多余的子项 */
  ellipsis?: boolean
  /** 是否将弹出菜单插入至 body 元素 */
  popperAppendToBody?: boolean
  /** 弹出菜单的偏移量 */
  popperOffset?: number
  /** 是否使用 vue-router 的模式 */
  router?: boolean
  /** 菜单项数据 */
  menuItems?: MenuItem[]
}

interface Emits {
  /** 菜单激活回调 */
  select: [index: string, indexPath: string[], item: MenuItem]
  /** sub-menu 展开的回调 */
  open: [index: string, indexPath: string[]]
  /** sub-menu 收起的回调 */
  close: [index: string, indexPath: string[]]
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  defaultActive: '',
  defaultOpeneds: () => [],
  uniqueOpened: false,
  menuTrigger: 'hover',
  mode: 'vertical',
  collapse: false,
  backgroundColor: '',
  textColor: '',
  activeTextColor: '',
  collapseTransition: true,
  ellipsis: true,
  popperAppendToBody: true,
  popperOffset: 6,
  router: false,
  menuItems: () => []
})

// 定义 emits
const emit = defineEmits<Emits>()

/**
 * 菜单激活事件处理
 */
const handleSelect = (index: string, indexPath: string[]): void => {
  const item = findMenuItem(props.menuItems, index)
  if (item) {
    emit('select', index, indexPath, item)
  }
}

/**
 * 子菜单展开事件处理
 */
const handleOpen = (index: string, indexPath: string[]): void => {
  emit('open', index, indexPath)
}

/**
 * 子菜单收起事件处理
 */
const handleClose = (index: string, indexPath: string[]): void => {
  emit('close', index, indexPath)
}

/**
 * 根据 index 查找菜单项
 */
const findMenuItem = (items: MenuItem[], index: string): MenuItem | null => {
  for (const item of items) {
    if (item.index === index) {
      return item
    }
    if (item.children) {
      const found = findMenuItem(item.children, index)
      if (found) {
        return found
      }
    }
  }
  return null
}
</script>

<script lang="ts">
/**
 * 菜单项递归组件
 */
import { defineComponent, h } from 'vue'

const MenuItemComponent = defineComponent({
  name: 'MenuItem',
  props: {
    item: {
      type: Object as () => MenuItem,
      required: true
    }
  },
  setup(props) {
    return () => {
      const { item } = props

      // 子菜单
      if (item.children && item.children.length > 0) {
        return h(
          'el-sub-menu',
          {
            index: item.index,
            disabled: item.disabled,
            popperClass: item.popperClass,
            showTimeout: item.showTimeout,
            hideTimeout: item.hideTimeout
          },
          {
            title: () => [
              item.icon ? h('el-icon', () => h(item.icon)) : null,
              h('span', item.title)
            ],
            default: () => item.children!.map(child =>
              h(MenuItemComponent, { key: child.index, item: child })
            )
          }
        )
      }

      // 菜单组
      if (item.type === 'group') {
        return h(
          'el-menu-item-group',
          { title: item.title },
          () => item.children!.map(child =>
            h(MenuItemComponent, { key: child.index, item: child })
          )
        )
      }

      // 分割线
      if (item.type === 'divider') {
        return h('li', { class: 'menu-divider' })
      }

      // 普通菜单项
      return h(
        'el-menu-item',
        {
          index: item.index,
          route: item.route,
          disabled: item.disabled
        },
        () => [
          item.icon ? h('el-icon', () => h(item.icon)) : null,
          h('template', { slot: 'title' }, () => [
            h('span', item.title),
            item.badge ? h(
              'el-badge',
              {
                value: item.badge.value,
                type: item.badge.type,
                isDot: item.badge.isDot,
                class: 'menu-badge'
              }
            ) : null
          ])
        ]
      )
    }
  }
})

export { MenuItemComponent as MenuItem }
</script>

<style scoped>
.menu-divider {
  height: 1px;
  margin: 8px 0;
  background-color: var(--el-border-color-lighter);
  list-style: none;
}

.menu-badge {
  margin-left: 8px;
}

/* 自定义样式变量 */
:deep(.el-menu) {
  border-right: none;
}

:deep(.el-menu-item) {
  display: flex;
  align-items: center;
  padding: 0 20px;
  height: 48px;
  line-height: 48px;
  font-size: 14px;
  transition: all 0.2s;
}

:deep(.el-menu-item .el-icon) {
  margin-right: 8px;
  font-size: 16px;
}

:deep(.el-menu-item:hover) {
  background-color: var(--el-menu-hover-bg-color);
}

:deep(.el-menu-item.is-active) {
  background-color: var(--el-menu-active-bg-color);
  color: var(--el-menu-active-color);
  border-right: 3px solid var(--el-color-primary);
}

:deep(.el-sub-menu .el-sub-menu__title) {
  display: flex;
  align-items: center;
  padding: 0 20px;
  height: 48px;
  line-height: 48px;
  font-size: 14px;
}

:deep(.el-sub-menu .el-sub-menu__title .el-icon) {
  margin-right: 8px;
  font-size: 16px;
}

:deep(.el-sub-menu .el-sub-menu__title:hover) {
  background-color: var(--el-menu-hover-bg-color);
}

:deep(.el-menu-item-group__title) {
  padding: 8px 20px 8px 20px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 水平模式样式 */
:deep(.el-menu--horizontal) {
  border-bottom: 1px solid var(--el-border-color-lighter);
}

:deep(.el-menu--horizontal .el-menu-item) {
  height: 60px;
  line-height: 60px;
  border-bottom: 3px solid transparent;
  border-right: none;
}

:deep(.el-menu--horizontal .el-menu-item.is-active) {
  border-bottom-color: var(--el-color-primary);
  border-right: none;
  background-color: transparent;
}

/* 折叠模式样式 */
:deep(.el-menu--collapse) {
  width: 64px;
}

:deep(.el-menu--collapse .el-menu-item) {
  padding: 0 20px;
  justify-content: center;
}

:deep(.el-menu--collapse .el-menu-item .el-icon) {
  margin-right: 0;
}

:deep(.el-menu--collapse .el-sub-menu .el-sub-menu__title) {
  padding: 0 20px;
  justify-content: center;
}

:deep(.el-menu--collapse .el-sub-menu .el-sub-menu__title .el-icon) {
  margin-right: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  :deep(.el-menu-item) {
    height: 44px;
    line-height: 44px;
    padding: 0 16px;
  }

  :deep(.el-sub-menu .el-sub-menu__title) {
    height: 44px;
    line-height: 44px;
    padding: 0 16px;
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  .menu-divider {
    background-color: var(--el-border-color-dark);
  }
}
</style>
